import { FC, useContext, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useCreateLocalization from "src/Pages/Company/OrganizationStructure/api/useCreateLocalization";
import useChangeLocalization from "src/Pages/Company/OrganizationStructure/api/useChangeLocalization";
import __ from "src/utils/Translations";
import { Controller, useForm } from "react-hook-form";
import { Box, TextField } from "@mui/material";
import { getErrorMessages } from "src/Pages/Company/OrganizationStructure/utils/errors/errorMessages";
import { handleBackendErrors } from "src/Pages/Company/OrganizationStructure/utils/errors/handleBackendErrors";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { localizationSchema } from "src/Pages/Company/OrganizationStructure/Localizations/Modal/Localization.schema";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import {
  LocalizationFormData,
  EditLocalizationModalProps,
} from "src/Pages/Company/OrganizationStructure/Localizations/localizations.models";

const EditLocalizationModal: FC<EditLocalizationModalProps> = ({
  companyId,
  structureId,
  mode,
  data,
  onClose,
}) => {
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const isEditMode = mode === "edit";

  const logStatus = useLogStatus();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isValid },
  } = useForm<LocalizationFormData>({
    defaultValues: {
      streetAddress: data?.streetAddress || "",
      postalCode: data?.postalCode || "",
      city: data?.city || "",
    },
    mode: "onChange",
    resolver: standardSchemaResolver(localizationSchema),
  });

  const watchedFields = watch(["city", "postalCode", "streetAddress"]);
  const hasAnyValue = watchedFields.some((value) => value?.trim()?.length > 0);
  const isSaveDisabled = !hasAnyValue || !isValid;

  const { mutate: createLocalization, isPending: isCreating } =
    useCreateLocalization(companyId, structureId);
  const { mutate: editLocalization, isPending: isEditing } =
    useChangeLocalization(companyId, structureId);

  const isPending = isEditMode ? isEditing : isCreating;

  const handleServerError = useCallback(
    (error: any) => {
      logStatus({
        type: NewRelicLogLevel.ERROR,
        message: `LocalizationModal: ${
          isEditMode ? "editLocalization" : "createLocalization"
        } failed`,
        statusDetails: {
          source: "API",
          operation: isEditMode ? "editLocalization" : "createLocalization",
          status: (error as AxiosError)?.response?.status,
          url: (error as AxiosError)?.config?.url,
        },
        errorObject: error instanceof Error ? error : new Error(String(error)),
      });

      const backendErrors = error?.response?.data?.errors;

      const backendFieldToFormField: Record<
        string,
        keyof LocalizationFormData
      > = {
        streetAddress: "streetAddress",
        postalCode: "postalCode",
        city: "city",
      };

      if (backendErrors && Array.isArray(backendErrors)) {
        const processed = handleBackendErrors<LocalizationFormData>({
          errors: backendErrors,
          errorMessages: getErrorMessages(),
          context: "LOCALIZATION",
        });

        processed.forEach(({ field, message }) => {
          const fieldName: keyof LocalizationFormData =
            backendFieldToFormField[field] || "streetAddress";

          setError(fieldName, {
            type: "server",
            message,
          });
        });
      } else {
        setError("streetAddress", {
          type: "server",
          message: __("Wystąpił błąd"),
        });
      }
    },
    [setError]
  );

  const handleSave = useCallback(
    (formValues: LocalizationFormData) => {
      const formData = Object.fromEntries(
        Object.entries({
          streetAddress: formValues.streetAddress,
          postalCode: formValues.postalCode,
          city: formValues.city,
        })
          .filter(
            ([_, value]) =>
              value != null &&
              typeof value === "string" &&
              value.trim().length > 0
          )
          .map(([key, value]) => [key, value.trim()])
      );

      if (Object.keys(formData).length === 0) return;

      const onSuccess = () => {
        resetModal();
        reset();
        if (onClose) onClose();
      };

      isEditMode
        ? editLocalization(
            {
              localizationId: data?.id || "",
              formData,
            },
            {
              onSuccess,
              onError: handleServerError,
            }
          )
        : createLocalization(formData, {
            onSuccess,
            onError: handleServerError,
          });
    },
    [
      isEditMode,
      createLocalization,
      editLocalization,
      data?.id,
      resetModal,
      reset,
      onClose,
      handleServerError,
    ]
  );

  const onCancel = useCallback(() => {
    resetModal();
    reset();
    if (onClose) onClose();
  }, [resetModal, reset, onClose]);

  const handleSaveClick = useCallback(
    () => handleSubmit(handleSave)(),
    [handleSubmit, handleSave]
  );

  const errorKeys = Object.keys(errors) as (keyof LocalizationFormData)[];
  const messages = errorKeys
    .map((key) => errors[key]?.message)
    .filter(Boolean) as string[];
  const uniqueMessages = Array.from(new Set(messages));
  const firstMessage = uniqueMessages[0] || "";

  const customElements = (
    <Box>
      <Box display="flex" gap={3} flexWrap="wrap" mt={2}>
        <Box flex="2 1 0">
          <Controller
            name="city"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={__("Miasto")}
                fullWidth
                error={!!fieldState.error}
                slotProps={{
                  htmlInput: {
                    "data-testid": "os__localizations-modal-city-input",
                  },
                }}
              />
            )}
          />
        </Box>

        <Box flex="1 1 0">
          <Controller
            name="postalCode"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={__("Kod pocztowy")}
                fullWidth
                error={!!fieldState.error}
                slotProps={{
                  htmlInput: {
                    "data-testid": "os__localizations-modal-postalcode-input",
                  },
                }}
              />
            )}
          />
        </Box>

        <Box flex="1 1 100%" mb={1}>
          <Controller
            name="streetAddress"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={__("Ulica i numer")}
                fullWidth
                error={!!fieldState.error}
                helperText={firstMessage}
                slotProps={{
                  formHelperText: {
                    sx: { color: "#D32F2F", mt: 3 },
                  },
                  htmlInput: {
                    "data-testid": "os__localizations-modal-street-input",
                  },
                }}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );

  useEffect(() => {
    setModal({
      open: true,
      title: isEditMode ? __("Edytuj lokalizację") : __("Nowa lokalizacja"),
      maxWidth: 628,
      disableExitButton: true,
      disableOutsideClick: true,
      customElements,
      actions: [
        {
          label: __("Anuluj"),
          variant: "outlined" as const,
          callbackFn: onCancel,
        },
        {
          label: __("Zapisz"),
          variant: "contained" as const,
          callbackFn: handleSaveClick,
          loading: isPending,
          disabled: isSaveDisabled,
        },
      ],
    });
  }, [
    setModal,
    customElements,
    onCancel,
    handleSaveClick,
    isPending,
    isSaveDisabled,
    isEditMode,
  ]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      resetModal();
    };
  }, [resetModal]);

  return null;
};

export default EditLocalizationModal;
